import { useCallback, useState } from "react";

import { datadogRum } from "@datadog/browser-rum";
import { useNavigate } from "react-router";
import { useShallow } from "zustand/react/shallow";

import { CollaborativeInboundUpdateState } from "analytics/events";
import { routes } from "config/routes";
import { useFetchUnitsSizesLazyQuery } from "flows/Inbound/stores/useInboundProductSearchService";

import { useInboundEventTracker } from "./useInboundEventTracker/useInboundEventTracker";
import {
  ListVerificationCheckTypeKeys,
  SerializedListVerificationCheck,
} from "../models/listVerificationCheck/types";
import { useInboundStore } from "../stores/inboundStore/useInboundStore";
import {
  extractInboundPlan,
  selectDespatchAdviceItemBySku,
  sumStockUpdatePlanTotal,
} from "../stores/inboundStore/utils";

export function useGenerateVerificationChecks() {
  const navigate = useNavigate();
  const { sendInboundStateUpdate } = useInboundEventTracker();
  const [isGeneratingChecks, setIsGeneratingChecks] = useState(false);
  const {
    deliveryType,
    deliverySSCC,
    creatorId,
    sharedListId,
    inboundUnitsMap,
    verificationChecks,
    inboundUnitsStockUpdates,
    despatchAdviceItems,
    setListVerificationChecks,
    setInboundUIState,
  } = useInboundStore(
    useShallow((state) => ({
      deliveryType: state.deliveryType,
      deliverySSCC: state.deliverySSCC,
      creatorId: state.creatorId,
      sharedListId: state.sharedListId,
      inboundUnitsMap: state.inboundUnitsMap,
      verificationChecks: state.verificationChecks,
      inboundUnitsStockUpdates: state.inboundUnitsStockUpdates,
      despatchAdviceItems: state.despatchAdviceItems,
      setListVerificationChecks: state.setListVerificationChecks,
      setInboundUIState: state.setInboundUIState,
    })),
  );

  const fetchUnitsSizes = useFetchUnitsSizesLazyQuery();

  const getNextMultipleOfHUSize = (qty: number, size: number) => Math.ceil(qty / size) * size;

  // Generate verification checks
  // eslint-disable-next-line react-compiler/react-compiler
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const generateChecks = async () => {
    let newChecks: SerializedListVerificationCheck[] = [...verificationChecks];
    // Check if all active checks are completed
    const allActiveCompleted = newChecks.every((c) => !c.isActive || c.isCompleted);

    if (allActiveCompleted) {
      // Set isActive to false for all checks if all active checks are completed
      newChecks = newChecks.map((check) => ({
        ...check,
        isActive: false,
      }));
    }
    const skusInStockUpdateState = Object.keys(inboundUnitsStockUpdates);
    const handlingUnitSizes = await fetchUnitsSizes(skusInStockUpdateState, true);

    // Generate new checks
    skusInStockUpdateState.forEach((sku) => {
      const { stockUpdatePlan, stockUpdated } = inboundUnitsStockUpdates[sku];

      if (stockUpdated) return;

      const inboundPlan = extractInboundPlan(stockUpdatePlan);
      if (!inboundPlan) return;

      const { quantityDelta } = inboundPlan;
      const desadvItem = selectDespatchAdviceItemBySku(sku, despatchAdviceItems);

      const totalQty = desadvItem?.totalQuantity ?? null;
      const huSizes = handlingUnitSizes[sku] ?? [];
      const totalPlan = sumStockUpdatePlanTotal(stockUpdatePlan);

      const hasDesadv = !!desadvItem;
      const isQtyOne = quantityDelta === 1;
      const expectedIsOne = totalQty === 1;

      const isNonMultipleOfHU =
        huSizes.length > 0 && huSizes.every((size) => quantityDelta % size !== 0);

      const intendedChecks: {
        checkType: ListVerificationCheckTypeKeys;
        data?: any;
      }[] = [];

      // 🔹 Quantity = 1 rule
      if (isQtyOne && (!hasDesadv || !expectedIsOne)) {
        intendedChecks.push({
          checkType: ListVerificationCheckTypeKeys.InboundQuantityOfOne,
        });
      }

      // 🔹 Handling unit mismatch
      else if (isNonMultipleOfHU && totalPlan !== 0) {
        const expected =
          huSizes.length === 1
            ? getNextMultipleOfHUSize(quantityDelta, huSizes[0]) - (quantityDelta - totalPlan)
            : undefined;

        intendedChecks.push({
          checkType: ListVerificationCheckTypeKeys.HandlingUnitQuantityMismatch,
          data: { quantity: totalPlan, expected },
        });
      }

      // 🔹 Large inbound quantity
      else if (quantityDelta > 50) {
        intendedChecks.push({
          checkType: ListVerificationCheckTypeKeys.LargeInboundQuantity,
          data: { quantity: quantityDelta },
        });
      }

      // 🔹 >50% above DESADV expected
      else if (hasDesadv && totalQty !== null && quantityDelta > totalQty * 1.5) {
        intendedChecks.push({
          checkType: ListVerificationCheckTypeKeys.MoreThan50PercentAboveExpected,
          data: { quantity: quantityDelta },
        });
      }

      // 🔹 Sync checks
      if (intendedChecks.length === 0) {
        newChecks = newChecks.filter((c) => c.sku !== sku);
        return;
      }

      intendedChecks.forEach(({ checkType, data }) => {
        const key = `${checkType}-${sku}-${data?.quantity ?? 1}`;
        if (!newChecks.some((c) => c.key === key)) {
          newChecks.push({
            key,
            sku,
            type: checkType,
            isCompleted: false,
            isActive: true,
            data,
          });
        }
      });

      const intendedKeys = intendedChecks.map(
        ({ checkType, data }) => `${checkType}-${sku}-${data?.quantity ?? 1}`,
      );

      newChecks = newChecks.filter((c) => c.sku !== sku || intendedKeys.includes(c.key));
    });

    return newChecks.filter(({ sku }) => !!inboundUnitsMap[sku]);
  };

  const handleGenerateChecks = useCallback(async () => {
    setIsGeneratingChecks(true);
    setInboundUIState({
      isProductAlreadyAddedBannerVisible: false,
    });
    try {
      const newChecks = await generateChecks();
      sendInboundStateUpdate({
        state: CollaborativeInboundUpdateState.ListVerificationStarted,
        droppingListId: sharedListId,
        sscc: deliverySSCC,
        inboundingType: deliveryType,
        creatorId,
      });
      setListVerificationChecks(newChecks);
      navigate(routes.inbound.listVerification);
    } catch (_) {
      datadogRum.addError("Failed to generate verification checks.");
    } finally {
      setIsGeneratingChecks(false);
    }
  }, [
    creatorId,
    deliverySSCC,
    deliveryType,
    generateChecks,
    navigate,
    sendInboundStateUpdate,
    setInboundUIState,
    setListVerificationChecks,
    sharedListId,
  ]);

  return {
    generateChecks: handleGenerateChecks,
    isGeneratingChecks,
  };
}
